import type { Question } from "@/lib/questions";
import type { UniversityLinkQuestion } from "@/lib/universityLink/types";

// 質問選択画面（/practice/select・/interview/select）で扱う質問の共通の形（#1861）。
//
// 共通質問は静的マスター（数値 ID）、大学が登録した質問は API（UUID）から来る。
// 数値前提の既存の型（Question / scoreMap / sortQuestions）は変えず、選択状態・並び・
// 描画の鍵だけをこの判別可能ユニオンで統一する。

export interface PresetSelectable {
  kind: "preset";
  key: string;
  question: Question;
}

export interface UniversitySelectable {
  kind: "university";
  key: string;
  id: string;
  text: string;
  /** 大学が設定したカテゴリ。未設定なら null */
  category: string | null;
}

export type SelectableQuestion = PresetSelectable | UniversitySelectable;

/** 「大学専用」の行で「すべて」（大学質問だけを全件）を選んだ値 */
export const UNIVERSITY_FILTER_ALL = "all";
/** カテゴリ未設定の大学質問を選ぶ値。カテゴリ名と衝突しない形にする */
export const UNIVERSITY_FILTER_UNCATEGORIZED = "__uncategorized__";
export const UNIVERSITY_UNCATEGORIZED_LABEL = "未分類";

export function presetKey(id: number): string {
  return `p:${id}`;
}

export function universityKey(id: string): string {
  return `u:${id}`;
}

export function toPresetSelectable(question: Question): PresetSelectable {
  return { kind: "preset", key: presetKey(question.id), question };
}

export function toUniversitySelectable(question: UniversityLinkQuestion): UniversitySelectable {
  return {
    kind: "university",
    key: universityKey(question.id),
    id: question.id,
    text: question.text,
    category: question.category,
  };
}

interface ChipOption {
  value: string;
  label: string;
}

/**
 * 「大学専用」の行の選択肢。「すべて」＋大学が設定したカテゴリ（登録順・重複除去）＋
 * カテゴリ未設定の質問があれば「未分類」。大学質問が無ければ空（行を出さない）。
 */
export function universityCategoryOptions(
  questions: ReadonlyArray<UniversityLinkQuestion>,
): ChipOption[] {
  if (questions.length === 0) return [];
  const categories: string[] = [];
  let hasUncategorized = false;
  for (const question of questions) {
    if (question.category) {
      if (!categories.includes(question.category)) categories.push(question.category);
    } else {
      hasUncategorized = true;
    }
  }
  return [
    { value: UNIVERSITY_FILTER_ALL, label: "すべて" },
    ...categories.map((category) => ({ value: category, label: category })),
    ...(hasUncategorized
      ? [{ value: UNIVERSITY_FILTER_UNCATEGORIZED, label: UNIVERSITY_UNCATEGORIZED_LABEL }]
      : []),
  ];
}

/**
 * 「大学専用」の行の選択に合う大学質問を返す。
 * null（未選択）と "all" は全件、カテゴリ名はそのカテゴリ、未分類はカテゴリ未設定。
 * 並びは API の順（新しい順）をそのまま保つ。
 */
export function filterUniversityQuestions(
  questions: ReadonlyArray<UniversityLinkQuestion>,
  selection: string | null,
): UniversitySelectable[] {
  return questions
    .filter((question) => {
      if (selection === null || selection === UNIVERSITY_FILTER_ALL) return true;
      if (selection === UNIVERSITY_FILTER_UNCATEGORIZED) return question.category === null;
      return question.category === selection;
    })
    .map(toUniversitySelectable);
}

/** 大学質問を先頭に、共通質問（並び替え済み）を後ろに並べる。 */
export function orderSelectableQuestions(
  university: ReadonlyArray<UniversitySelectable>,
  presets: ReadonlyArray<Question>,
): SelectableQuestion[] {
  return [...university, ...presets.map(toPresetSelectable)];
}

/** 面接の選択状態（鍵の配列）を、開始 API の2つのパラメータに分ける。 */
export function splitSelectedKeys(keys: ReadonlyArray<string>): {
  questionIds: number[];
  universityQuestionIds: string[];
} {
  const questionIds: number[] = [];
  const universityQuestionIds: string[] = [];
  for (const key of keys) {
    if (key.startsWith("p:")) questionIds.push(Number(key.slice(2)));
    else if (key.startsWith("u:")) universityQuestionIds.push(key.slice(2));
  }
  return { questionIds, universityQuestionIds };
}
